import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import PageLoading from '../components/PageLoading';
import { getBriefingConditional, getTopicDetails } from '../services/topics';
import { createDigestHighlight, getAllDigestHighlights } from '../services/highlights';
import useLocalMutation from '../hooks/useLocalMutation';
import useOverlayedMutations from '../hooks/useOverlayedMutations';
import { overlayTopicDigest } from '../db/overlay';
import { useToast } from '../hooks/useToast';
import { useLocalFirstQuery } from '../hooks/useLocalFirstQuery';
import { BRIEFING_TTL_MS } from '../db/freshness';
import { setTitle } from '../utils/environment';
import { renderDigestMarkdown } from '../utils/markdown';
import { formatFullDate } from '../utils/dateUtils';
import { ArticleLink, TopicLink } from '../components/SmartLink';

const Briefing = () => {
  const articleNavContext = { source: 'briefing' };
  const { showError } = useToast();
  const [expandedTopics, setExpandedTopics] = useState({});
  const [markedAsRead, setMarkedAsRead] = useState({});
  const [markingInProgress, setMarkingInProgress] = useState({});
  const [digestHighlights, setDigestHighlights] = useState([]);
  const [highlightingItem, setHighlightingItem] = useState(null);
  const prevBriefingRef = useRef(null);
  const { mutate } = useLocalMutation();
  const { mutations: pendingReads } = useOverlayedMutations({
    types: ['mark_read', 'mark_unread', 'mark_all_read'],
  });

  const fetchFn = useCallback(async ({ etag }) => {
    return getBriefingConditional({ etag, limit: 10, include_misc: true });
  }, []);

  const { data: briefingData, loading, error: fetchError } = useLocalFirstQuery({
    table: 'meta',
    key: 'topic-briefing',
    ttl: BRIEFING_TTL_MS,
    fetch: fetchFn,
  });

  const digestsView = useMemo(
    () => (briefingData?.digests || []).map(d => overlayTopicDigest(d, pendingReads)),
    [briefingData, pendingReads],
  );
  const error = fetchError?.message || null;

  // Initialize expanded state when briefing data first arrives.
  useEffect(() => {
    if (!briefingData || briefingData === prevBriefingRef.current) return;
    prevBriefingRef.current = briefingData;
    const expanded = {};
    (briefingData.digests || []).forEach(d => { expanded[d.topic_id] = true; });
    setExpandedTopics(expanded);
  }, [briefingData]);

  const toggleTopic = (topicId) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topicId]: !prev[topicId]
    }));
  };

  const handleMarkAllRead = async (e, topicId) => {
    e.stopPropagation();

    // Don't do anything if already marked or in progress
    if (markedAsRead[topicId] || markingInProgress[topicId]) return;

    setMarkingInProgress(prev => ({ ...prev, [topicId]: true }));

    try {
      const limit = 100;
      let offset = 0;
      let hasMore = true;
      while (hasMore) {
        const detail = await getTopicDetails(topicId, { unread_only: true, limit, offset });
        const ids = (detail.articles || []).map(a => a.id);
        if (ids.length === 0) break;
        await mutate({
          type: 'mark_all_read',
          payload: { topic_id: String(topicId), article_ids: ids },
        });
        if (ids.length < limit) {
          hasMore = false;
        } else {
          offset += ids.length;
        }
      }
      setMarkedAsRead(prev => ({ ...prev, [topicId]: true }));
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      // Could add error toast here if needed
    } finally {
      setMarkingInProgress(prev => ({ ...prev, [topicId]: false }));
    }
  };

  const fetchDigestHighlights = async () => {
    try {
      const response = await getAllDigestHighlights();
      // Flatten all highlights from all topics into a single array
      const allHighlights = [];
      if (response.topics) {
        Object.values(response.topics).forEach(topicData => {
          if (topicData.highlights) {
            allHighlights.push(...topicData.highlights);
          }
        });
      }
      setDigestHighlights(allHighlights);
    } catch (err) {
      console.error('Failed to fetch digest highlights:', err);
    }
  };

  const handleHighlightDigestItem = async (e, digestText, section, topicLabel, generatedAt, articleId = null) => {
    e.stopPropagation();

    const itemKey = `${topicLabel}-${section}-${digestText}`;
    if (highlightingItem === itemKey) return;

    setHighlightingItem(itemKey);

    try {
      const highlightData = {
        article_id: articleId,  // When set: shows on BOTH article page AND topic page (dual display)
        topic_name: topicLabel, // Always set: shows on topic page
        digest_section: section,
        digest_text: digestText,
        digest_version: generatedAt,
      };

      await createDigestHighlight(highlightData);
      await fetchDigestHighlights();
    } catch (err) {
      console.error('Failed to highlight digest item:', err);
      showError(err.message || 'Failed to create highlight');
    } finally {
      setHighlightingItem(null);
    }
  };

  const isDigestItemHighlighted = (digestText) => {
    return digestHighlights.some(h => h.exact_text === digestText);
  };

  useEffect(() => {
    setTitle('Briefing · Read It Never');
    fetchDigestHighlights();
  }, []);

  if (loading) {
    return <PageLoading message="Loading briefing..." />;
  }

  return (
    <div className="min-h-screen bg-clay-bg">
      <PageHeader
        emoji="📋"
        title="Briefing"
        subtitle={`Your daily reading summary across ${digestsView.length} topics`}
        backTo="/dashboard"
        backLabel="Back to dashboard"
        actions={
          <Link
            to="/topics"
            className="btn-clay-ghost text-sm font-medium"
          >
            View All Topics →
          </Link>
        }
      />

      {/* Content */}
      <div className="max-w-4xl mx-auto px-2 sm:px-6 lg:px-8 py-8">
        {/* Error Message */}
        {error && (
          <div className="notice-clay-error flex items-center gap-2 mb-6">
            <span className="text-xl" role="img" aria-hidden="true">😅</span>
            <span className="font-medium">{error}</span>
          </div>
        )}

        {digestsView.length === 0 ? (
          /* Empty State */
          <div className="card-clay-static p-12 text-center">
            <div className="text-6xl mb-4 animate-bounce-gentle">📋</div>
            <h2 className="font-display text-lg font-semibold text-heading mb-2">No briefing available</h2>
            <p className="text-secondary-text mb-6 max-w-md mx-auto">
              Your briefing is generated when you have unread articles in topic clusters.
              Try adding some articles first.
            </p>
            <Link
              to="/topics"
              className="btn-clay inline-flex items-center"
            >
              View Topics
            </Link>
          </div>
        ) : (
          /* Digest Cards */
          <div className="space-y-6">
            {digestsView.map((digest, index) => (
              <div
                key={digest.topic_id}
                className="card-clay-static !p-0 overflow-hidden"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Topic Header */}
                <div
                  className="px-2 py-3 sm:p-4 border-b-2 border-clay-border cursor-pointer hover:bg-surface-hover transition-colors"
                  onClick={() => toggleTopic(digest.topic_id)}
                >
                  {/* Title Row */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <span className={`text-lg text-tertiary-text transition-transform duration-200 flex-shrink-0 ${
                        expandedTopics[digest.topic_id] ? 'rotate-0' : '-rotate-90'
                      }`}>
                        ▼
                      </span>
                      <h2 className="min-w-0 break-words">
                        <TopicLink
                          topicId={digest.topic_id}
                          context={{ source: 'briefing' }}
                          onClick={(e) => e.stopPropagation()}
                          className="font-display text-lg font-semibold text-primary-600 hover:text-primary-700 cursor-pointer no-underline"
                        >
                          {digest.topic_label}
                        </TopicLink>
                      </h2>
                      {digest.unread_count > 0 && !markedAsRead[digest.topic_id] && (
                        <span className="badge-fun-sky animate-bounce-gentle flex-shrink-0">
                          {digest.unread_count}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={(e) => handleMarkAllRead(e, digest.topic_id)}
                      disabled={markedAsRead[digest.topic_id] || markingInProgress[digest.topic_id] || digest.unread_count === 0}
                      className={`p-2 rounded-clay border-2 transition-all ${
                        markedAsRead[digest.topic_id] || digest.unread_count === 0
                          ? 'border-fun-mint bg-emerald-50 dark:bg-emerald-950/30 text-fun-mint'
                          : 'border-theme-border-subtle text-tertiary-text hover:text-fun-mint hover:border-fun-mint hover:bg-emerald-50 dark:hover:bg-emerald-900/30'
                      } disabled:opacity-30 disabled:cursor-not-allowed`}
                      title={markedAsRead[digest.topic_id] || digest.unread_count === 0 ? 'All read' : 'Mark all as read'}
                    >
                      {markingInProgress[digest.topic_id] ? (
                        <div className="spinner-fun-sm w-5 h-5"></div>
                      ) : markedAsRead[digest.topic_id] || digest.unread_count === 0 ? (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {/* Metadata Row */}
                  <div className="flex flex-wrap items-center text-sm text-secondary-text ml-7 sm:ml-8 gap-3 sm:gap-4">
                    <span className="badge-fun-lavender text-xs">{digest.unread_count} unread</span>
                    <span className="badge-fun-mint text-xs">{digest.article_count} analyzed</span>
                    <span className="text-tertiary-text">Updated {formatFullDate(digest.generated_at)}</span>
                    {digest.unread_count > 0 && digest.unread_count < digest.article_count && !markedAsRead[digest.topic_id] && (
                      <span className="text-xs text-tertiary-text opacity-60">
                        · {digest.article_count - digest.unread_count} already read
                      </span>
                    )}
                  </div>
                </div>

                {/* Digest Content (Collapsible) */}
                {expandedTopics[digest.topic_id] && (
                  <div className="px-2 py-4 sm:p-6 space-y-6">
                    {/* What's New */}
                    {digest.whats_new && digest.whats_new.length > 0 && (
                      <div>
                        <h3 className="font-display text-md font-bold text-heading mb-3 flex items-center">
                          <span className="text-lg mr-2">📰</span>
                          What&apos;s New
                        </h3>
                        <ul className="space-y-2">
                          {digest.whats_new.map((item, idx) => {
                            // Handle both new format (object with text/article_id) and legacy (string)
                            const text = typeof item === 'string' ? item : item.text;
                            const articleId = typeof item === 'object' ? item.article_id : null;
                            const isHighlighted = isDigestItemHighlighted(text);
                            const itemKey = `${digest.topic_label}-whats_new-${text}`;
                            const isHighlighting = highlightingItem === itemKey;

                            return (
                              <li
                                key={idx}
                                className={`group text-sm text-body-text leading-relaxed pl-2 sm:pl-4 border-l-2 ${
                                  isHighlighted ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-950/30 py-2 pr-2 rounded-r' : 'border-blue-300'
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">{renderDigestMarkdown(text, articleId, articleNavContext)}</div>
                                  <button
                                    onClick={(e) => handleHighlightDigestItem(e, text, 'whats_new', digest.topic_label, digest.generated_at, articleId)}
                                    disabled={isHighlighted || isHighlighting}
                                    className={`ml-2 flex-shrink-0 px-2 py-1 text-xs rounded transition-colors ${
                                      isHighlighted
                                        ? 'text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/40 cursor-default'
                                        : 'text-tertiary-text hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 opacity-0 group-hover:opacity-100'
                                    }`}
                                    title={isHighlighted ? 'Already highlighted' : 'Highlight this item'}
                                  >
                                    {isHighlighting ? '⋯' : isHighlighted ? '★' : '☆'}
                                  </button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* Core Theme */}
                    {digest.core_theme && digest.core_theme.length > 0 && (
                      <div>
                        <h3 className="font-display text-md font-bold text-heading mb-3 flex items-center">
                          <span className="text-lg mr-2">🎯</span>
                          Core Theme
                        </h3>
                        <ul className="space-y-2">
                          {digest.core_theme.map((item, idx) => {
                            const isHighlighted = isDigestItemHighlighted(item);
                            const itemKey = `${digest.topic_label}-core_theme-${item}`;
                            const isHighlighting = highlightingItem === itemKey;

                            return (
                              <li
                                key={idx}
                                className={`group text-sm text-body-text leading-relaxed pl-2 sm:pl-4 border-l-2 ${
                                  isHighlighted ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-950/30 py-2 pr-2 rounded-r' : 'border-purple-300'
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">{renderDigestMarkdown(item)}</div>
                                  <button
                                    onClick={(e) => handleHighlightDigestItem(e, item, 'core_theme', digest.topic_label, digest.generated_at)}
                                    disabled={isHighlighted || isHighlighting}
                                    className={`ml-2 flex-shrink-0 px-2 py-1 text-xs rounded transition-colors ${
                                      isHighlighted
                                        ? 'text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/40 cursor-default'
                                        : 'text-tertiary-text hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 opacity-0 group-hover:opacity-100'
                                    }`}
                                    title={isHighlighted ? 'Already highlighted' : 'Highlight this item'}
                                  >
                                    {isHighlighting ? '⋯' : isHighlighted ? '★' : '☆'}
                                  </button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* Recommended Reads */}
                    {digest.recommended_reads && digest.recommended_reads.length > 0 && (
                      <div>
                        <h3 className="font-display text-md font-bold text-heading mb-3 flex items-center">
                          <span className="text-lg mr-2">⭐</span>
                          Recommended Reads
                        </h3>
                        <div className="space-y-2">
                          {digest.recommended_reads.map((read, idx) => {
                            const isHighlighted = isDigestItemHighlighted(read.reason);
                            const itemKey = `${digest.topic_label}-recommended_reads-${read.reason}`;
                            const isHighlighting = highlightingItem === itemKey;

                            return (
                              <ArticleLink
                                key={idx}
                                articleId={read.article_id}
                                context={articleNavContext}
                                className={`group block p-2 sm:p-3 rounded-clay border-2 transition-all cursor-pointer hover:shadow-clay-sm no-underline ${
                                  isHighlighted
                                    ? 'bg-amber-50 dark:bg-amber-950/30 border-fun-lemon'
                                    : 'bg-accent-50 dark:bg-accent-950/30 border-accent-200 dark:border-accent-800 hover:border-accent-400'
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="font-medium text-heading text-sm mb-1">
                                    {read.title}
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleHighlightDigestItem(e, read.reason, 'recommended_reads', digest.topic_label, digest.generated_at);
                                    }}
                                    disabled={isHighlighted || isHighlighting}
                                    className={`ml-2 flex-shrink-0 px-2 py-1 text-xs rounded transition-colors ${
                                      isHighlighted
                                        ? 'text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/40 cursor-default'
                                        : 'text-tertiary-text hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 opacity-0 group-hover:opacity-100'
                                    }`}
                                    title={isHighlighted ? 'Already highlighted' : 'Highlight this reason'}
                                  >
                                    {isHighlighting ? '⋯' : isHighlighted ? '★' : '☆'}
                                  </button>
                                </div>
                                <div className="text-sm text-secondary-text italic">
                                  {renderDigestMarkdown(read.reason)}
                                </div>
                              </ArticleLink>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Footer Actions */}
                    <div className="pt-4 border-t-2 border-clay-border flex items-center justify-between gap-2">
                      <TopicLink
                        topicId={digest.topic_id}
                        context={{ source: 'briefing' }}
                        className="btn-clay-ghost text-sm font-medium no-underline"
                      >
                        View all articles in {digest.topic_label} →
                      </TopicLink>
                      <button
                        onClick={(e) => handleMarkAllRead(e, digest.topic_id)}
                        disabled={markedAsRead[digest.topic_id] || markingInProgress[digest.topic_id] || digest.unread_count === 0}
                        className={`btn-clay text-sm ${
                          markedAsRead[digest.topic_id] || digest.unread_count === 0
                            ? 'bg-emerald-100 dark:bg-emerald-900/40 border-fun-mint text-emerald-700 dark:text-emerald-300'
                            : 'bg-fun-mint border-emerald-400 text-white hover:bg-emerald-500'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {markingInProgress[digest.topic_id]
                          ? 'Marking...'
                          : markedAsRead[digest.topic_id] || digest.unread_count === 0
                            ? '✨ All Read'
                            : '✓ Mark All Read'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Briefing;
